import { TInputDependencyCheckboxInstance } from './InputDependencyCheckbox';

/**
 * @description Toggle Checkbox Implementation
 * @param AjaxForm extended Form
 */
function FormDependencyCheckboxesMixinCreator(AjaxForm: import('widgets/forms/AjaxForm').TAjaxForm) {
    /**
     * @category widgets
     * @subcategory forms
     * @class FormDependencyCheckboxesMixin
     * @augments AjaxForm
     * @see AjaxForm
     * @classdesc Represents AjaxForm Mixin for a form with DependencyCheckboxes:
     * If the Form contains an {@link InputDependencyCheckbox}, those Checkboxes can trigger the {@link FormDependencyCheckboxesMixin#handleDependentCheckboxes} function.
     * The function will then handle all dependencies for children and parents (i.e. check all children if a parent is checked)
     *
     * See {@link InputDependencyCheckbox} for details on how to configure it in dynamicForms.js
     *
     * This Mixin contains the business logic for the checkbox propagation:
     *  - Checkboxes can have a parent-child relation which connects them
     *  - Parent-child relation can be nested (parent => child => child etc.), children can also be parents of other children
     *  - Parent checkboxes control all their children (check/uncheck all according to parent checkbox)
     *  - Parent => Child propagation is recursively propagated
     *  - Child checkboxes control their parent checkboxes:
     *     - If unchecked, the parent(s) is/are unchecked as well (recursively)
     *     - If all children are checked, the parent checkbox is checked as well (recursively)
     *
     * @property {string} data-widget - Widget name `ajaxform`
     * @property {string} data-event-submit - Event listener for form submission
     */

    const ATTR_DATA_WIDGET = 'data-widget';

    class FormDependencyCheckboxesMixin extends AjaxForm {
        /**
         * Handle Dependent Checkboxes: Called when a dependent checkbox was checked/unchecked
         * Note: Function needs to be configured as a widget event for a dependent checkbox
         *
         * @param checkbox The Checkbox receiving the handle event
         */
        handleDependentCheckboxes(checkbox: TInputDependencyCheckboxInstance) {
            checkbox.validate();

            if (checkbox.isParent()) {
                this.handleParent(checkbox);
            }

            if (checkbox.isDependent()) {
                this.handleDependent(checkbox);
            }
        }

        onCheckboxChange(checkbox: TInputDependencyCheckboxInstance) {
            if (checkbox) {
                const checked = checkbox.getValue();
                const uuid = checkbox.id?.replace('checkbox-', '');
                const reasonContainer = this.ref(`selectReason-form-lineItem-${uuid}`);

                const select = this.items.filter(item => item.ref('self').attr(ATTR_DATA_WIDGET) === 'inputSelect');
                const reasonSelectWidget = select.find(item => item.ref('self').attr('id') === `reason-${uuid}`);
                const reasonSelect = reasonSelectWidget?.ref<HTMLSelectElement>('self').get();
                const subReasonFieldsets = this.ref('self')?.get()?.querySelectorAll('.reason-subreason');
                const errorLabel = this.ref(`reasonSelect-${uuid}`).get() as HTMLLabelElement;
                const subReasonErrorLabel = this.ref(`subreason-${uuid}`).get() as HTMLLabelElement;
                const selectedPli = this.ref(`summaryLineItem-${uuid}`).get() as HTMLLabelElement;

                if (!checked) {
                    const errorMessageLabel = this.ref(`errorMessageLabel`);

                    errorMessageLabel?.hide();
                    reasonContainer?.hide();

                    return;
                }

                selectedPli?.removeAttribute('hidden');
                reasonContainer?.show();
                reasonSelect?.addEventListener('change', () => {
                    if (reasonSelect.value.trim()) {
                        const optionKey = reasonSelect.value.trim();

                        errorLabel.textContent = '';
                        errorLabel.setAttribute('hidden', 'hidden');
                        subReasonErrorLabel.textContent = '';
                        subReasonErrorLabel?.setAttribute('hidden', 'hidden');
                        reasonSelect.classList.remove('b-invalid');

                        reasonSelect
                            ?.closest('.reason-container')
                            ?.querySelectorAll('.reason-subreason.b-invalid')
                            .forEach(item => item.classList.remove('b-invalid'));

                        reasonSelect
                            ?.closest('.reason-container')
                            ?.querySelectorAll('fieldset[data-type="subReason"]')
                            .forEach(subReasonDiv => {
                                const htmlDiv = subReasonDiv as HTMLElement;
                                const divId = htmlDiv?.dataset.ref;

                                htmlDiv.hidden = divId !== optionKey;
                            });
                    }

                    reasonSelect?.addEventListener('click', function () {
                        if (this.value === '') {
                            let currentFs;

                            subReasonErrorLabel.textContent = '';
                            subReasonErrorLabel.setAttribute('hidden', 'hidden');

                            subReasonFieldsets?.forEach(item => {
                                currentFs = item as HTMLFieldSetElement;

                                currentFs.hidden = true;
                            });
                        }
                    });
                });

                // if one checked then remove red from all other checkboxes
                const checkboxWidgets = this.items.filter(item => item.ref('self').attr(ATTR_DATA_WIDGET) === 'inputDependencyCheckbox');

                checkboxWidgets.forEach(widget => {
                    const box = widget.ref('field')?.get() as HTMLInputElement | null;

                    if (box && box.id.startsWith('checkbox-')) {
                        box.classList.remove('b-invalid');
                    }
                });
            }
        }

        getChildren(): Array<string> {
            const checkboxWidgets = this.items.filter(item => item.ref('self').attr(ATTR_DATA_WIDGET) === 'inputDependencyCheckbox');

            const checkboxIds = checkboxWidgets
                .map(widget => widget.id)
                .filter((id): id is string => typeof id === 'string' && id.startsWith('checkbox-'));

            return checkboxIds;
        }

        /**
         * Handles a Parent checkbox.
         * Checks all the child checkboxes if checked, unchecks all child checkboxes if unchecked.
         *
         * @param checkbox The Parent Checkbox to process
         */
        handleParent(checkbox: TInputDependencyCheckboxInstance) {
            const childConfig = checkbox.config.children;
            const children =
                typeof childConfig === 'string' && childConfig.trim().length > 0 ? childConfig.split(',').map(id => id.trim()) : this.getChildren();

            children.forEach(childID => {
                this.getById(childID, (child: TInputDependencyCheckboxInstance) => {
                    // Silently update the child checkbox
                    child.setValue(checkbox.getValue(), true);
                    this.onCheckboxChange(child);

                    // Validate child checkbox. Required to display errors for mandatory checkboxes.
                    child.validate();

                    if (child.isParent()) {
                        // Propagate ths event to the Parent, so its parents are updated too
                        this.handleParent(child);
                    }
                });
            });
        }

        /**
         * Handles a dependent checkbox
         *   - Unchecks its parent checkboxes
         *   - Calls a check on parent checkboxes.
         *     A parent should be marked as checked if all of its children are checked
         *
         * Recursively propagates to parents.
         *
         * @param checkbox The dependent Checkbox to process
         */
        handleDependent(checkbox: TInputDependencyCheckboxInstance) {
            const parentId = checkbox.getParent();

            this.getById(parentId, (parent: TInputDependencyCheckboxInstance) => {
                if (checkbox.getValue()) {
                    /**
                     * Unchecked => Checked handler
                     *
                     * In case the current checkbox is checked, inform the parent
                     * checkbox about it. If all of the sibling checkboxes are checked too,
                     * the parent checkbox will be checked as well.
                     */
                    this.handleAllDependents(parent);
                } else {
                    /**
                     * Checked => Unchecked handler
                     *
                     * Uncheck the parent Checkbox
                     */
                    parent.setValue('', true);
                    this.onCheckboxChange(checkbox);
                }

                if (parent.isDependent()) {
                    /**
                     * If the parent checkbox also is a dependent,
                     * Propagate the event to its parent (recursive)
                     */
                    this.handleDependent(parent);
                }
            });
        }

        /**
         * Dependents handler function.
         *
         * Checks the status of all dependents of a parent.
         *  - If all dependents are checked, the parent is checked as well
         *  - If at least one depentent is unchecked, nothing is done.
         *
         * Recursively handles children.
         *
         * @param checkbox The dependent Checkbox to process
         */
        handleAllDependents(checkbox: TInputDependencyCheckboxInstance) {
            let allChecked = true;
            const childConfig = checkbox.config.children;
            const children =
                typeof childConfig === 'string' && childConfig.trim().length > 0 ? childConfig.split(',').map(id => id.trim()) : this.getChildren();

            children.forEach(childID => {
                // Read values from all children and look for unchecked boxes
                this.getById(childID, (child: TInputDependencyCheckboxInstance) => {
                    this.onCheckboxChange(child);

                    if (!allChecked) {
                        // We already found one unchecked child => skip further lookup
                        return;
                    }

                    if (child.isParent()) {
                        /**
                         * If a child is a parent at the same time, check all of its children too.
                         * As soon as any child is unchecked, "allChecked" needs to be false.
                         */
                        allChecked = this.handleAllDependents(child);
                    }

                    if (!child.getValue()) {
                        // As soon as a single child is not checked, "allChecked" is set to false
                        allChecked = false;
                    }
                });
            });

            if (allChecked) {
                // If all children are found checked, we can check the parent
                checkbox.setValue('checked', true);
            }

            return allChecked;
        }
    }

    return FormDependencyCheckboxesMixin;
}

export type TFormDependencyCheckboxesMixin = ReturnType<typeof FormDependencyCheckboxesMixinCreator>;

export type TFormDependencyCheckboxesMixinInstance = InstanceType<TFormDependencyCheckboxesMixin>;

export default FormDependencyCheckboxesMixinCreator;
